<script setup lang="ts">
  import { ref, computed, watch, ComponentPublicInstance } from 'vue';
  import CustomSearchSelectInput from '../CustomSearchSelectInput.vue';
  import type { Option } from '@/models/interfaces';

  type CustomSearchSelectInputExposed = {
    searchRef?: HTMLInputElement;
  };

  type CustomSearchSelectInputInstance = ComponentPublicInstance<
    typeof CustomSearchSelectInput,
    CustomSearchSelectInputExposed
  >;

  defineProps({
    index: {
      type: String,
      required: true,
    },
  });

  /*
   * Validity can be check from outside
   * using @input and the exposed checkValidity method.
   */
  const emit = defineEmits(['input']);

  const value = defineModel<string>('value');
  const required = defineModel<boolean>('required');
  const disabled = defineModel<boolean>('disabled');

  const inputRef = ref<CustomSearchSelectInputInstance>();
  const selectedOption = ref<Option>({
    value: value.value || '',
    label: value.value || '',
    disabled: false,
  });

  const tlds = ['gmail.com', 'hotmail.com', 'outlook.com', 'orange.fr'];
  const options = computed<Array<Option>>(() => {
    let buffer: Array<string> = [];
    let rawUserInput = value.value || '';
    if (rawUserInput) {
      const [before, after] = rawUserInput.split('@');

      if (before) rawUserInput = before;

      buffer = tlds.filter((tld) => ('@' + tld).includes('@' + after));
      buffer = buffer.map((tld) => before + '@' + tld);
    }

    return buffer.map((tld) => {
      return {
        value: tld,
        label: tld,
        disabled: false,
      };
    });
  });

  // Displays the tlds options on user input interaction.
  watch(
    () => value.value,
    () => {
      selectedOption.value = {
        value: value.value || '',
        label: value.value || '',
        disabled: false,
      };
      emit('input');
    },
  );

  function checkValidity(): boolean {
    const input = inputRef.value?.searchRef;
    if (!(input instanceof HTMLInputElement)) return false;
    input.checkValidity();

    const validity = {
      badInput: input.validity.badInput,
      customError: input.validity.customError,
      patternMismatch: input.validity.patternMismatch,
      rangeOverflow: input.validity.rangeOverflow,
      rangeUnderflow: input.validity.rangeUnderflow,
      stepMismatch: input.validity.stepMismatch,
      tooLong: input.validity.tooLong,
      tooShort: input.validity.tooShort,
      typeMismatch: input.validity.typeMismatch || input.value === '',
    };

    const flags = Object.values(validity);
    return !flags.some((flag) => flag);
  }

  defineExpose({
    checkValidity,
  });
</script>

<template>
  <custom-search-select-input
    ref="inputRef"
    :index="index"
    :standalone="true"
    :type="'email'"
    :placeholder="$t('client_info.email')"
    v-model:search-value="value"
    v-model:options="options"
    v-model:selected-option="selectedOption"
    v-model:required="required"
    v-model:disabled="disabled"
    :is-filtered-on-search="true"
    :display-chevron="false"
  />
</template>
